import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  type DefaultContext,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import type {FetchResult, Operation} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {onError} from '@apollo/client/link/error';
import {getMainDefinition} from '@apollo/client/utilities';
import {createConsumer} from '@rails/actioncable';
import {Kind, OperationTypeNode} from 'graphql';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import type {TokenStore} from '~/lib/auth';
import {getCableUrl, getSharingApiUrl, parseRegion} from '~/lib/config';

function createCableUrl(identifier: string, token: string): string {
  const url = new URL(getCableUrl(parseRegion(identifier)));
  url.searchParams.set('token', token);

  return url.toString();
}

function isSubscriptionOperation(operation: {query: unknown}): boolean {
  const definition = getMainDefinition(
    operation.query as Parameters<typeof getMainDefinition>[0],
  );
  return (
    definition.kind === Kind.OPERATION_DEFINITION &&
    definition.operation === OperationTypeNode.SUBSCRIPTION
  );
}

/**
 * Creates a fresh ActionCable consumer and ActionCableLink per subscription
 * operation, disconnecting when the subscription completes or is unsubscribed.
 *
 * This gives each subscription a fresh auth token, and keeps the viewer's
 * authorization window as short as the chat turn itself.
 *
 * One consumer per operation is only workable because chat is the only
 * subscription on the shared surface. A second one needs a persistent
 * connection with reconnection and token refresh (see the main app's
 * `src/lib/graphql/subscriptions.ts`).
 */
class TransientCableLink extends ApolloLink {
  constructor(
    private readonly getCableUrl: () => string,
    private readonly channelName: string,
    private readonly actionName: string,
  ) {
    super();
  }

  request(operation: Operation): Observable<FetchResult> {
    const cable = createConsumer(this.getCableUrl);
    const inner = new ActionCableLink({
      cable,
      channelName: this.channelName,
      actionName: this.actionName,
    });

    const source = inner.request(operation, () => Observable.of());
    if (!source) {
      cable.disconnect();
      return new Observable((observer) => observer.complete());
    }

    return new Observable((observer) => {
      const subscription = source.subscribe({
        next: (value) => observer.next(value),
        error: (err) => observer.error(err),
        complete: () => observer.complete(),
      });

      return () => {
        subscription.unsubscribe();
        cable.disconnect();
      };
    });
  }
}

// Hand-maintained: every interface/union in sharing/schema.graphql must be
// listed or fragment matching silently degrades.
export const SHARING_POSSIBLE_TYPES = {
  EntityInterface: ['Canvas', 'StubEntity'],
  CanvasBlockInterface: [
    'ChartBlock',
    'DocumentsBlock',
    'ListBlock',
    'MeetingSummaryBlock',
    'MetricBlock',
    'RichTextBlock',
    'ScheduleMeetingBlock',
    'TextBlock',
  ],
  CanvasEntityInterface: ['CanvasEntityDocumentShare', 'CanvasEntityImage'],
};

function isForbidden(error: unknown): boolean {
  return (
    error instanceof Error && 'statusCode' in error && error.statusCode === 403
  );
}

interface CreateApolloClientOptions {
  /**
   * The API re-checks page access on every HTTP request, so a 403 here means
   * the JWT's access was revoked mid-session (page made restricted, grant
   * removed) or the JWT lapsed before its refresh fired. Called with the token
   * the refused request carried. Resolving true means a newer token is in
   * place and the request is retried with it; false lets the error through.
   */
  onAccessDenied?: (deniedJwt: string) => Promise<boolean>;
}

export function createApolloClient(
  identifier: string,
  tokenStore: TokenStore,
  {onAccessDenied}: CreateApolloClientOptions = {},
) {
  const region = parseRegion(identifier);

  const httpLink = createHttpLink({
    uri: getSharingApiUrl(region),
  });

  const authLink = setContext((_, context: DefaultContext) => {
    const jwt = tokenStore.jwt;
    return {
      jwt,
      headers: {
        ...(context.headers as Record<string, string>),
        Authorization: `Bearer ${jwt}`,
      },
    };
  });

  const accessDeniedLink = onError(({networkError, operation, forward}) => {
    if (!onAccessDenied || !isForbidden(networkError)) {
      return;
    }
    const {jwt} = operation.getContext() as {jwt?: string};
    if (jwt === undefined) {
      return;
    }

    return new Observable<FetchResult>((observer) => {
      let retry: {unsubscribe: () => void} | undefined;
      onAccessDenied(jwt).then(
        (recovered) => {
          if (recovered) {
            retry = forward(operation).subscribe(observer);
          } else {
            observer.error(networkError);
          }
        },
        (err: unknown) => observer.error(err),
      );
      return () => retry?.unsubscribe();
    });
  });

  const cableLink = new TransientCableLink(
    () => createCableUrl(identifier, tokenStore.jwt),
    'Sharing::Cable::AnyCableGraphqlChannel',
    'execute',
  );

  const link = ApolloLink.split(
    isSubscriptionOperation,
    cableLink,
    ApolloLink.from([accessDeniedLink, authLink, httpLink]),
  );

  const cache = new InMemoryCache({
    possibleTypes: SHARING_POSSIBLE_TYPES,
  });

  return new ApolloClient({
    link,
    cache,
    connectToDevTools: import.meta.env.DEV,
    name: 'sharing',
  });
}
